import {FC} from 'react'

import {IpOfficePriceIncreaseAlert} from '@/components/alerts/IpOfficePriceIncreaseAlert'
import {PriceWarningTooltip} from '@/components/common/form/tooltip/PriceWarningTooltip'
import {useFormSummaryContent} from '@/content/formSummary'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {Jurisdiction} from '@/utils/countries/constants'
import {alpha2ToName} from '@/utils/countries/translations'
import {Currency} from '@/utils/currency'
import {useLanguage} from '@/utils/i18n/language'
import {hasPriceWarningTooltip} from '@/utils/pricing'
import {getActiveIpOfficePriceIncreaseInfo} from '@/utils/registration-request/utils'
import {USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {FormSummaryItemBreakdown} from './layout/FormSummaryItemBreakdown'
import {FormSummaryItemBreakdownRow} from './layout/FormSummaryItemBreakdownRow'

type FormSummaryCountryDetailProps = {
  jurisdiction: Jurisdiction
  totalPrice: number
  serviceFee: number
  administrativeFee: number
  trackedEvent?: {name: string; parameters?: any}
  currency?: Currency
  isPriceTooltipVisible?: boolean
  isExpanded?: boolean
  textColor?: string
}

export const FormSummaryCountryDetail: FC<
  React.PropsWithChildren<FormSummaryCountryDetailProps>
> = ({
  jurisdiction,
  currency,
  totalPrice,
  serviceFee,
  administrativeFee,
  trackedEvent,
  isPriceTooltipVisible = true,
  isExpanded,
  textColor,
  children,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {serviceFeeTitle, administrativeFeeTitle} = useFormSummaryContent()
  const hasTooltip = hasPriceWarningTooltip(jurisdiction) && isPriceTooltipVisible

  const ipOfficePriceIncreaseInfo = getActiveIpOfficePriceIncreaseInfo().find(
    (info) => info.jurisdiction === jurisdiction,
  )

  return (
    <>
      <FormSummaryItemBreakdown
        title={alpha2ToName(jurisdiction, language)}
        price={totalPrice}
        currency={currency}
        trackedEvent={trackedEvent}
        tooltip={hasTooltip && <PriceWarningTooltip country={jurisdiction} />}
        dataTest="CountryPriceDetail"
        breakdownItems={
          <>
            <FormSummaryItemBreakdownRow
              title={t(serviceFeeTitle)}
              price={serviceFee}
              currency={currency}
            />
            <FormSummaryItemBreakdownRow
              title={t(administrativeFeeTitle)}
              price={administrativeFee}
              currency={currency}
            />
            {children}
          </>
        }
        isExpanded={isExpanded}
        textColor={textColor}
      >
        {ipOfficePriceIncreaseInfo && (
          <div className="alert-wrapper">
            <IpOfficePriceIncreaseAlert {...ipOfficePriceIncreaseInfo} />
          </div>
        )}
      </FormSummaryItemBreakdown>

      <style jsx>{`
        .alert-wrapper {
          margin-top: ${theme.spacing[2.5]};
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .alert-wrapper {
            display: none;
          }
        }
      `}</style>
    </>
  )
}
